import { Controller } from "stimulus";
import Rails from "@rails/ujs";

let debounce = require('lodash/debounce');

// Selection is tracked as a mode plus a small id set, the way the admin data
// table does it, so it stays correct without knowing every id on the client:
//
//   specific    -> only the ids in the set are attending (the default; seeded
//                  with the already-attending learners)
//   none        -> nobody
//   all         -> everybody in the program (the server expands it)
//   all_except  -> everybody except the ids in the set
//
// This makes "select all" cover learners that infinite scroll hasn't loaded
// yet, and keeps the submit correct regardless of the active search.
export default class extends Controller {
  static targets = ['button', 'list', 'search', 'selectAll', 'checkbox'];
  static values = {
    url: String,
    saveUrl: String,
    attendingIds: Array,
    confirmTitle: String,
    confirmMessage: String,
    confirmAllow: String,
    confirmCancel: String
  };

  initialize() {
    this.search = debounce(this.search.bind(this), 300);
  }

  open() {
    this.mode = 'specific';
    this.ids = new Set(this.attendingIdsValue.map(Number));
    this.query = '';
    this.searching = false;

    this.buttonTarget.classList.add('hidden');
    this.listTarget.classList.remove('hidden');

    this.load(this.urlValue);
  }

  search(event) {
    this.query = event.target.value;
    this.searching = true;

    this.load(`${this.urlValue}?query=${encodeURIComponent(this.query)}`);
  }

  toggle(event) {
    const id = Number(event.target.value);
    const checked = event.target.checked;

    switch (this.mode) {
      case 'all':
        if (!checked) { this.mode = 'all_except'; this.ids = new Set([id]); }
        break;
      case 'none':
        if (checked) { this.mode = 'specific'; this.ids = new Set([id]); }
        break;
      case 'all_except':
        checked ? this.ids.delete(id) : this.ids.add(id);
        if (this.ids.size === 0) this.mode = 'all';
        break;
      default: // specific
        checked ? this.ids.add(id) : this.ids.delete(id);
        if (this.ids.size === 0) this.mode = 'none';
    }

    this.refreshSelectAll();
  }

  selectAll(event) {
    this.mode = event.target.checked ? 'all' : 'none';
    this.ids = new Set();

    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = event.target.checked;
    });
  }

  save(event) {
    if (this.removesAttendance()) {
      Rails.confirm(null, event.currentTarget, this.confirmDataset(), {
        confirmCallback: () => this.submit()
      });
      return;
    }

    this.submit();
  }

  submit() {
    const params = new URLSearchParams();
    params.append('selection_mode', this.mode);
    this.ids.forEach((id) => params.append('membership_ids[]', id));

    Rails.ajax({
      url: this.saveUrlValue,
      type: 'PATCH',
      data: params.toString(),
      dataType: 'script'
    });
  }

  // True when a learner who was attending when the list opened is no longer
  // selected — i.e. the facilitator is removing attendance.
  removesAttendance() {
    return this.attendingIdsValue.some((id) => !this.isSelected(Number(id)));
  }

  confirmDataset() {
    return {
      confirmTitle: this.confirmTitleValue,
      confirmMessage: this.confirmMessageValue,
      confirmAllow: this.confirmAllowValue,
      confirmCancel: this.confirmCancelValue
    };
  }

  cancel() {
    this.close();
  }

  close() {
    this.listTarget.innerHTML = '';
    this.listTarget.classList.add('hidden');
    this.buttonTarget.classList.remove('hidden');
  }

  // --- target lifecycle: rows arrive via the initial open, a search re-render,
  // or infinite-scroll appends, so re-apply the tracked selection to whatever
  // checkboxes are now on screen. ---

  checkboxTargetConnected(checkbox) {
    checkbox.checked = this.isSelected(Number(checkbox.value));
  }

  selectAllTargetConnected() {
    this.refreshSelectAll();
  }

  searchTargetConnected(input) {
    if (!this.searching) return;

    input.value = this.query;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }

  // --- helpers ---

  load(url) {
    Rails.ajax({ url, type: 'GET', dataType: 'script' });
  }

  isSelected(id) {
    switch (this.mode) {
      case 'all': return true;
      case 'none': return false;
      case 'all_except': return !this.ids.has(id);
      default: return this.ids.has(id); // specific
    }
  }

  refreshSelectAll() {
    if (!this.hasSelectAllTarget) return;

    this.selectAllTarget.checked = this.mode === 'all';
  }
}
